import { DateTime, Info, InvalidZone, Settings, SystemZone, Zone } from 'luxon';

import {
  AddRemoveOperationBasic,
  DateSourceBasic,
  DateTimeFormatBasic,
  DateTimeFormatsMillisAndSecondsBasic,
  TimeUnit,
  TimeUnits
} from '../common';
import {
  InputRequiredError,
  InvalidAdjustmentOperationError,
  InvalidDateSourceError,
  UnparseableDateError,
  UnsupportedTimeZoneError
} from '../errors';

import { round } from './numeric';

Settings.defaultZone = 'UTC';

abstract class InvalidReason {
  static readonly UNPARSABLE = 'unparsable';
  static readonly UNSUPPORTED_ZONE = 'unsupported zone';
}

const systemZone = new SystemZone();
const invalidZone = new InvalidZone();

/**
 * Set the time zone of a Luxon DateTime instance only when a time zone or offset is specified. Otherwise, return the
 *   Luxon DateTime as-is.
 * @param dateTime A Luxon DateTime instance whose time zone to configure.
 * @param inputTimeZoneOrOffset The originating time zone of `timestamp` expressed as either a string, numeric
 *   offset, or Luxon zone. Default = UTC.
 * @param appendTimeZoneOrOffset If the time zone/offset should be appended such that it does not alter the underlying date/time.
 *
 * @see {@link https://moment.github.io/luxon/#/zones?id=specifying-a-zone|Luxon docs} for more information regarding the supported zone formats.
 */
function configureZone(
  dateTime: DateTime,
  inputTimeZoneOrOffset?: string | number | Zone,
  appendTimeZoneOrOffset: boolean = false
): DateTime {
  if (inputTimeZoneOrOffset) {
    const inputZone = Info.normalizeZone(inputTimeZoneOrOffset);
    return dateTime.setZone(inputZone, { keepLocalTime: appendTimeZoneOrOffset });
  }
  return dateTime;
}

/**
 * Create a Luxon DateTime instance from an ISO-8601 date/time, custom formatted date/time, or a Unix epoch.
 * @param timestamp An ISO-8601 date/time, SQL date/time, custom formatted date/time, or a Unix epoch. If provided as a
 *   string that is not in ISO-8601 or SQL format, `inputFormatString` must be provided.
 * @param inputFormat The format of `timestamp`. Default = automatically determined based on the value of `timestamp`.
 * @param inputFormatString The format string used to parse `timestamp`. Default = any valid ISO-8601 format.
 * @param inputTimeZoneOrOffset The originating time zone of `timestamp` expressed as either a string, numeric
 *   offset, or Luxon zone. It is important to note that this time zone/offset will be appended to the parsed timestamp
 *   only if the time zone information is not present in `timestamp`. If `timestamp` contains the time zone information,
 *   that time zone will be used instead and the value of this argument will be ignored. Default = UTC.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 * @see {@link https://moment.github.io/luxon/#/zones?id=specifying-a-zone|Luxon docs} for more information regarding the supported zone formats.
 */
function getInputDateTime<F extends `${DateTimeFormatBasic}`, TU extends `${DateTimeFormatsMillisAndSecondsBasic}`>(
  timestamp: string | number,
  inputFormat?: DateTimeFormatBasic | DateTimeFormatsMillisAndSecondsBasic | F | TU,
  inputFormatString?: string,
  inputTimeZoneOrOffset?: string | number | Zone
): DateTime {
  let numericTimestamp: number | undefined;
  let timestampString: string | undefined;

  if (typeof timestamp === 'string') {
    timestampString = timestamp;
    numericTimestamp = Number(timestamp);
    if (Number.isNaN(numericTimestamp)) {
      // note: parse the timestamp using an invalid time zone
      // this is used below to determine if the timestamp contains the time zone information so that it can be appended if not
      let parsedDateTime = getParsedDatetime(
        timestamp,
        inputFormat as DateTimeFormatBasic,
        inputFormatString,
        invalidZone
      );
      let appendTimeZoneOrOffset;

      if (parsedDateTime.invalidReason === InvalidReason.UNPARSABLE) {
        throw new UnparseableDateError();
      }

      if (parsedDateTime.zoneName) {
        // the timestamp contains the zone information
        appendTimeZoneOrOffset = false;
      } else {
        // the timestamp does not contain the zone information

        /*
         * Because we've already parsed the timestamp above, we can convert it to an object and use that to create a
         * new instance such that the parsed information is preserved (prevents having to re-parse). However, because
         * we used an invalid zone above, we must set the zone to the system zone when creating the new instance as that's
         * what Luxon does internally. This prevents the timestamp from getting modified by Luxon when creating the new
         * DateTime instance.
         */
        parsedDateTime = getParsedDatetime(
          timestamp,
          inputFormat as DateTimeFormatBasic,
          inputFormatString,
          systemZone
        );
        appendTimeZoneOrOffset = true;
        inputTimeZoneOrOffset ??= Settings.defaultZone;
      }

      parsedDateTime = configureZone(parsedDateTime, inputTimeZoneOrOffset, appendTimeZoneOrOffset);

      if (parsedDateTime.invalidReason === InvalidReason.UNSUPPORTED_ZONE) {
        throw new UnsupportedTimeZoneError();
      }

      return parsedDateTime;
    }
  }

  if (numericTimestamp === undefined) {
    numericTimestamp = timestamp as number;
    timestampString = numericTimestamp.toString();
  }

  if (inputFormat) {
    let unit = TimeUnits.tryParse(inputFormat);
    if (!unit) {
      switch (inputFormat) {
        case DateTimeFormatBasic.milliseconds:
        default:
          unit = TimeUnits.milliseconds;
          break;
        case DateTimeFormatBasic.seconds:
          unit = TimeUnits.seconds;
          break;
      }
    }

    switch (unit) {
      case TimeUnits.milliseconds:
      default: {
        const dateTime = DateTime.fromMillis(numericTimestamp);
        return configureZone(dateTime, inputTimeZoneOrOffset);
      }
      case TimeUnits.seconds: {
        const dateTime = DateTime.fromSeconds(numericTimestamp);
        return configureZone(dateTime, inputTimeZoneOrOffset);
      }
    }
  }

  // assume 10-digit numbers or numbers with a decimal are seconds; milliseconds otherwise
  if (timestampString!.length <= 10 || timestampString!.indexOf('.') >= 0) {
    const dateTime = DateTime.fromSeconds(numericTimestamp);
    return configureZone(dateTime, inputTimeZoneOrOffset);
  } else {
    const dateTime = DateTime.fromMillis(numericTimestamp);
    return configureZone(dateTime, inputTimeZoneOrOffset);
  }
}

/**
 * Convert a Luxon DateTime instance to a string representation.
 * @param dateTime The Luxon DateTime instance.
 * @param outputFormatString The format string used to serialize `dateTime`. Default = full ISO-8601.
 * @param outputTimeZoneOrOffset The time zone or offset to which the resulting timestamp should be converted. Default = the time zone of `dateTime`.
 *
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 * @see {@link https://moment.github.io/luxon/#/zones?id=specifying-a-zone|Luxon docs} for more information regarding the supported zone formats.
 */
function getOutputDateTime(
  dateTime: DateTime,
  outputFormatString?: string,
  outputTimeZoneOrOffset?: string | number
): string {
  const outputZone = Info.normalizeZone(outputTimeZoneOrOffset ?? dateTime.zone);

  if (outputFormatString) {
    return dateTime.setZone(outputZone).toFormat(outputFormatString);
  }
  const parsedDateTime = dateTime.setZone(outputZone);
  if (parsedDateTime.invalidReason === InvalidReason.UNSUPPORTED_ZONE) {
    throw new UnsupportedTimeZoneError();
  }
  return parsedDateTime.toISO();
}

/**
 * Adjust an ISO-8601 date/time or a Unix epoch by adding/subtracting a specified amount of time.
 * @param timestamp An ISO-8601 date/time or a Unix epoch.
 * @param amount The amount of time by which `timestamp` should be adjusted (positive or negative values).
 * @param amountUnit The unit of measurement of `amount`.
 *
 * @returns The adjusted timestamp in ISO-8601 format.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function adjust<TU extends `${TimeUnit}`>(
  timestamp: string | number,
  amount: number,
  amountUnit: TimeUnit | TU
): string {
  let dateTime = getInputDateTime(timestamp);
  const timeUnit = TimeUnits.parse(amountUnit);
  switch (timeUnit) {
    case TimeUnits.milliseconds:
    default:
      dateTime = dateTime.plus({ milliseconds: amount });
      break;
    case TimeUnits.seconds:
      dateTime = dateTime.plus({ seconds: amount });
      break;
    case TimeUnits.minutes:
      dateTime = dateTime.plus({ minutes: amount });
      break;
    case TimeUnits.hours:
      dateTime = dateTime.plus({ hours: amount });
      break;
    case TimeUnits.days:
      dateTime = dateTime.plus({ days: amount });
      break;
    case TimeUnits.weeks:
      dateTime = dateTime.plus({ weeks: amount });
      break;
    case TimeUnits.months:
      dateTime = dateTime.plus({ months: amount });
      break;
    case TimeUnits.years:
      dateTime = dateTime.plus({ years: amount });
      break;
  }

  return getOutputDateTime(dateTime);
}

/**
 * Adjust an ISO-8601 date/time or a Unix epoch by adding/subtracting a specified amount of time.
 * @param timestamp An ISO-8601 date/time or a Unix epoch.
 * @param operation If `amount` should be added or subtracted
 * @param amount The amount of time by which `timestamp` should be adjusted (positive or negative values).
 * @param amountUnit The unit of measurement of `amount`.
 *
 * @returns The adjusted timestamp in ISO-8601 format.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {InvalidAdjustmentOperationError} "<operation>" is not a valid adjustment operation.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function adjustBasic<O extends `${AddRemoveOperationBasic}`, TU extends `${TimeUnit}`>(
  timestamp: string | number,
  operation: AddRemoveOperationBasic | O,
  amount: number,
  amountUnit: TimeUnit | TU
): string {
  switch (operation) {
    case AddRemoveOperationBasic.adding:
      return adjust(timestamp, amount, amountUnit);
    case AddRemoveOperationBasic.subtracting:
      return adjust(timestamp, amount * -1, amountUnit);
  }

  throw new InvalidAdjustmentOperationError();
}

/**
 * Append or overwrite the time zone or offset of an ISO-8601 date/time without adjusting the underlying date/time.
 * @param timestamp An ISO-8601 date/time.
 * @param timeZoneOrOffset The time zone or offset to which the resulting timestamp should be converted. Default = UTC.
 *
 * @returns The date/time in ISO-8601 format in the specified time zone.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function appendTimeZone(timestamp: string, timeZoneOrOffset?: string | number): string {
  const zone = Info.normalizeZone(timeZoneOrOffset ?? Settings.defaultZone);
  if (timeZoneOrOffset && !zone.isValid) {
    throw new UnsupportedTimeZoneError();
  }
  return getOutputDateTime(getInputDateTime(timestamp).setZone(zone, { keepLocalTime: true }));
}

/**
 * Convert an ISO-8601 date/time or a Unix epoch to an ISO-8601 date/time, custom formatted date/time, or a Unix epoch.
 * @param timestamp An ISO-8601 date/time or a Unix epoch.
 * @param outputFormat The format in which the converted timestamp should be returned. Default = ISO-8601.
 * @param outputFormatString If `outputFormat = 'Custom'`, the format string used to serialize the converted date/time. Default: ISO-8601.
 *
 * @returns `timestamp` converted to the specified output format.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function convert<F extends `${DateTimeFormatBasic}`>(
  timestamp: string | number,
  outputFormat: DateTimeFormatBasic | F,
  outputFormatString?: string
): string | number {
  const dateTime: DateTime = getInputDateTime(timestamp);
  switch (outputFormat) {
    case DateTimeFormatBasic.custom:
      if (!outputFormatString) {
        throw new InputRequiredError('outputFormatString');
      }
      return getOutputDateTime(dateTime, outputFormatString);
    case DateTimeFormatBasic.iso:
    default:
      return getOutputDateTime(dateTime);
    case DateTimeFormatBasic.milliseconds:
      return round(dateTime.toMillis(), '1');
    case DateTimeFormatBasic.seconds:
      return dateTime.toSeconds();
    case DateTimeFormatBasic.sql:
      return dateTime.toSQL({ includeOffset: false, includeOffsetSpace: false, includeZone: false });
  }
}

/**
 * Convert a Unix epoch with the specified unit to an ISO-8601 date/time.
 * @param epoch The number of milliseconds or seconds since the Unix epoch (specified by `unit`).
 * @param unit The unit of measurement `epoch`.
 *
 * @returns `epoch` converted to an ISO-8601 date/time.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function fromEpoch<TU extends `${DateTimeFormatsMillisAndSecondsBasic}`>(
  epoch: number,
  unit: DateTimeFormatsMillisAndSecondsBasic | TU
): string {
  return getOutputDateTime(getInputDateTime(epoch, unit));
}

/**
 * Parse a date/time and convert it to an ISO-8601 date/time.
 * @param timestamp An ISO-8601 date/time, SQL date/time, custom formatted date/time, or a Unix epoch. If provided as a
 *   string that is not in ISO-8601 or SQL format, `inputFormatString` must be provided.
 * @param inputFormat The format of `timestamp`. Default = automatically determined based on the value of `timestamp`.
 * @param inputFormatString The format string used to parse `timestamp`. Default = any valid ISO-8601 format.
 * @param inputTimeZoneOrOffset The originating time zone of `timestamp` expressed as either a string or numeric
 *   offset. It is important to note that this time zone/offset will be appended to the parsed timestamp
 *   only if the time zone information is not present in `timestamp`. If `timestamp` contains the time zone information,
 *   that time zone will be used instead and the value of this argument will be ignored. Default = UTC.
 *
 * @returns The parsed date/time in ISO-8601 format.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function parse<F extends `${DateTimeFormatBasic}`, TU extends `${DateTimeFormatsMillisAndSecondsBasic}`>(
  timestamp: string | number,
  inputFormat?: DateTimeFormatBasic | DateTimeFormatsMillisAndSecondsBasic | F | TU,
  inputFormatString?: string,
  inputTimeZoneOrOffset?: string | number
): string {
  return getInputDateTime(timestamp, inputFormat, inputFormatString, inputTimeZoneOrOffset).toISO();
}

/**
 * Parse a date/time and convert it to an ISO-8601 date/time.
 * @param dateSource The source of `timestamp`. If `current`, `timestamp` is not required.
 * @param timestamp An ISO-8601 date/time, SQL date/time, custom formatted date/time, or a Unix epoch. If provided as a
 *   string that is not in ISO-8601 or SQL format, `inputFormatString` must be provided. Not required if `dateSource` is
 *   `current`.
 * @param inputFormat The format of `timestamp`. Default = automatically determined based on the value of `timestamp`.
 * @param inputFormatString The format string used to parse `timestamp`. Default = any valid ISO-8601 format.
 * @param inputTimeZoneOrOffset The originating time zone of `timestamp` expressed as either a string or numeric
 *   offset. Default = UTC.
 *
 * @returns The parsed date/time in ISO-8601 format.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {InvalidDateSourceError} The date source is invalid.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function parseBasic<
  S extends `${DateSourceBasic}`,
  F extends `${DateTimeFormatBasic}`,
  TU extends `${DateTimeFormatsMillisAndSecondsBasic}`
>(
  dateSource: DateSourceBasic | S,
  timestamp?: string | number,
  inputFormat?: DateTimeFormatBasic | DateTimeFormatsMillisAndSecondsBasic | F | TU,
  inputFormatString?: string,
  inputTimeZoneOrOffset?: string | number
): string {
  switch (dateSource) {
    case DateSourceBasic.current:
      return DateTime.now().toISO();
    case DateSourceBasic.playbookData:
      if (!timestamp) {
        throw new InputRequiredError('timestamp');
      }
      return getInputDateTime(timestamp, inputFormat, inputFormatString, inputTimeZoneOrOffset).toISO();
  }

  throw new InvalidDateSourceError();
}

/**
 * Set the time zone or offset of an ISO-8601 date/time, adjusting the underlying date/time accordingly.
 * @param timestamp An ISO-8601 date/time.
 * @param timeZoneOrOffset The time zone or offset to which the resulting timestamp should be converted. Default = UTC.
 *
 * @returns The date/time in ISO-8601 format in the specified time zone.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function setTimeZone(timestamp: string, timeZoneOrOffset: string | number): string {
  return getOutputDateTime(getInputDateTime(timestamp), undefined, timeZoneOrOffset);
}

/**
 * Convert an ISO-8601 date/time or a Unix epoch to an ISO-8601 **date**.
 * @param timestamp An ISO-8601 date/time or a Unix epoch.
 *
 * @returns The date portion of `timestamp` in ISO-8601 format.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function toISODate(timestamp: string | number): string {
  return getInputDateTime(timestamp).toISODate();
}

/**
 * Convert an ISO-8601 date/time or a Unix epoch to an ISO-8601 **time**.
 * @param timestamp An ISO-8601 date/time or a Unix epoch.
 *
 * @returns The time portion of `timestamp` in ISO-8601 format.
 *
 * @throws {InputRequiredError} "<input name>" is a required input but was not provided.
 * @throws {UnparseableDateError} The date/time could not be parsed.
 * @throws {UnsupportedTimeZoneError} The time zone is not supported.
 */
export function toISOTime(timestamp: string | number): string {
  return getInputDateTime(timestamp).toISOTime();
}

function getParsedDatetime<F extends `${DateTimeFormatBasic}`>(
  timestamp: string,
  inputFormat?: DateTimeFormatBasic | F,
  inputFormatString?: string,
  zone?: string | Zone
): DateTime {
  let parsedDateTime: DateTime;
  switch (inputFormat ?? DateTimeFormatBasic.iso) {
    default:
    case DateTimeFormatBasic.custom: {
      if (!inputFormatString) {
        throw new InputRequiredError('inputFormatString');
      }
      parsedDateTime = DateTime.fromFormat(timestamp, inputFormatString, { setZone: true, zone: zone });
      break;
    }
    case DateTimeFormatBasic.iso: {
      parsedDateTime = DateTime.fromISO(timestamp, { setZone: true, zone: zone });
      break;
    }
    case DateTimeFormatBasic.sql: {
      parsedDateTime = DateTime.fromSQL(timestamp, { setZone: true, zone: zone });
      break;
    }
  }

  return parsedDateTime;
}
